"use client";

import { ArrowRightIcon, Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import Image from "next/image";
import { useRef, useEffect, useState, useCallback } from "react";
import { usePlayer } from "@/contexts/player-context";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useI18n } from "@/lib/i18n";
import { useEpisodeProgress } from "@/hooks/useEpisodeProgress";
import { useUser } from "@/lib/context/user-context";
import { useUserSettings } from "@/contexts/user-settings-context";
import { useRouter } from "next/navigation";
import { useIsMobile } from "@/hooks/use-mobile";

const ENABLE_DIRECT_GLOBAL_PLAYER = true;

// 用于避免 hydration 不匹配的 hook
function useHasMounted() {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  return hasMounted;
}

interface Episode {
  ownerAvatarUrl: string;
  author: string;
  title: string;
  shareId: string;
  userId?: string;
  videoId?: string;
  thumbnail?: string;
  isFeaturedEpisode?: boolean;
  duration?: number;
  durationFormatted?: string;
  titleTranslation?: string | Record<string, string>;
}

// Helper function to extract title translation string from string or object
function getTitleTranslationString(
  titleTranslation: string | Record<string, unknown> | null | undefined,
  userTargetLanguageCode: string = 'zh'
): string {
  if (!titleTranslation) return "";
  
  // If it's a string, return as-is
  if (typeof titleTranslation === "string") {
    return titleTranslation;
  }
  
  // If it's an object, get the translation for user's target language
  if (typeof titleTranslation === "object" && titleTranslation !== null) {
    // Try user's language first
    const userLangTranslation = titleTranslation[userTargetLanguageCode];
    if (userLangTranslation && typeof userLangTranslation === "string" && userLangTranslation.trim().length > 0) {
      return userLangTranslation;
    }
    
    // Fallback to Chinese if available
    const zhTranslation = titleTranslation["zh"];
    if (zhTranslation && typeof zhTranslation === "string" && zhTranslation.trim().length > 0) {
      return zhTranslation;
    }
    
    // Return first available string translation
    for (const value of Object.values(titleTranslation)) {
      if (typeof value === "string" && value.trim().length > 0) {
        return value;
      }
    }
  }
  
  return "";
}

interface EpisodesSectionProps {
  episodes: Episode[];
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

function EpisodeItem({
  episode,
  mobileFull = false,
  isCompleted = false
}: {
  episode: Episode;
  mobileFull?: boolean;
  isCompleted?: boolean;
}) {
  const hasMounted = useHasMounted();
  const { t } = useI18n();
  const {
    setCurrentEpisode,
    setCleanReadData,
    setIsLoading: setPlayerLoading,
    openFullPlayer,
    closeFullPlayer,
  } = usePlayer();
  const { userSettings } = useUserSettings();
  const { session } = useUser();
  const router = useRouter();
  const [isDirectLoading, setIsDirectLoading] = useState(false);
  const isMobile = useIsMobile();
  
  // 只在客户端 mounted 后且非移动端才启用 direct play
  // 移动端直接跳转到 episodes 页面
  const allowDirectPlay = hasMounted && ENABLE_DIRECT_GLOBAL_PLAYER && !isMobile;

  // 在 mounted 前使用默认值，确保服务端和客户端一致
  const preferChineseTitles = hasMounted ? userSettings?.preferChineseTitles : false;
  const userTargetLanguageCode = userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';
  const titleTranslationStr = getTitleTranslationString(episode.titleTranslation, userTargetLanguageCode);
  const displayTitle =
    preferChineseTitles && titleTranslationStr
      ? titleTranslationStr
      : episode.title;
  // 仅当用户语言设置为中文时才显示 tooltip
  const isChineseLanguage = userTargetLanguageCode === 'zh';
  const tooltipText = (() => {
    if (!isChineseLanguage) return undefined;
    if (!titleTranslationStr) return undefined;
    if (preferChineseTitles && titleTranslationStr) {
      return episode.title;
    }
    if (!preferChineseTitles) {
      return titleTranslationStr;
    }
    return undefined;
  })();

  const episodeUrl = episode.videoId
    ? `/episodes/${episode.shareId}?videoId=${episode.videoId}`
    : `/episodes/${episode.shareId}`;

  const attemptDirectGlobalPlay = useCallback(async () => {
    if (!allowDirectPlay) {
      return false;
    }

    if (isDirectLoading) {
      return true;
    }

    try {
      setIsDirectLoading(true);
      openFullPlayer();
      setPlayerLoading(true);

      const placeholderEpisode = {
        shareId: episode.shareId,
        content: {
          shareId: episode.shareId,
          title: episode.title,
          author: episode.author,
          ownerAvatarUrl: episode.ownerAvatarUrl,
          thumbnail: episode.thumbnail,
          videoId: episode.videoId,
          duration: episode.duration,
        },
      };

      setCurrentEpisode(placeholderEpisode);
      setCleanReadData(null);
      setPlayerLoading(true);

      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      const token =
        session?.access_token ??
        (typeof window !== "undefined"
          ? window.localStorage.getItem("token")
          : null);

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/episodes/${episode.shareId}`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch episode data: ${response.status}`);
      }

      const episodeData = await response.json();

      if (process.env.NODE_ENV !== "production") {
        const resolvedVocabulary =
          episodeData?.vocabulary ??
          episodeData?.content?.vocabulary ??
          episodeData?.content?.content?.vocabulary ??
          null;
        console.log("[episodes-section] fetched full episode", {
          shareId: episode.shareId,
          hasVocabulary: Boolean(resolvedVocabulary),
          vocabSource: resolvedVocabulary
            ? resolvedVocabulary === episodeData?.vocabulary
              ? "episode.vocabulary"
              : "nested"
            : "missing",
        });
      }
      setCurrentEpisode(episodeData);
      setCleanReadData(episodeData?.content?.content?.cleanRead ?? null);

      return true;
    } catch (error) {
      console.error("Unable to open episode in global player:", error);
      closeFullPlayer();
      return false;
    } finally {
      setPlayerLoading(false);
      setIsDirectLoading(false);
    }
  }, [
    episode,
    isDirectLoading,
    session?.access_token,
    setCleanReadData,
    setCurrentEpisode,
    setPlayerLoading,
    openFullPlayer,
    closeFullPlayer,
    allowDirectPlay,
  ]);

  const handleCardClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!allowDirectPlay) {
        return;
      }

      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
        return;
      }

      e.preventDefault();

      const handled = await attemptDirectGlobalPlay();
      if (!handled) {
        if (typeof window !== "undefined") {
          window.location.href = episodeUrl;
        } else {
          router.push(episodeUrl);
        }
      }
    },
    [attemptDirectGlobalPlay, episodeUrl, router, allowDirectPlay]
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={`flex-shrink-0 ${mobileFull ? 'w-[calc(100vw-2rem)]' : 'w-[400px] sm:w-[400px] md:w-[400px] lg:w-[400px]'} cursor-pointer group transition-all duration-200 hover:bg-primary/20 p-[3px] rounded-lg relative`}>
          <Link href={episodeUrl} onClick={handleCardClick}>
            {/* 水平卡片布局 */}
            <div className="flex gap-2">
              {/* 左侧缩略图 */}
              <div className="flex-shrink-0 w-[105px] h-[60px] rounded-md overflow-hidden relative">
                <div className="w-full h-full relative">
                  <Image
                    src={
                      episode.thumbnail ||
                      "https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=128&h=80&fit=crop&crop=center"
                    }
                    alt={displayTitle}
                    fill
                    className="object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>

                {allowDirectPlay && isDirectLoading && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-white" />
                  </div>
                )}

                {/* Ready Badge - Bottom right corner */}
                <div className="absolute bottom-1 right-1 z-10">
                  <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium">
                    Ready
                  </span>
                </div>

                {/* Duration badge - Show above Ready badge if exists */}
                {episode.durationFormatted && (
                  <div className="absolute bottom-1 right-1 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ transform: 'translateY(-100%)', marginBottom: '2px' }}>
                    {episode.durationFormatted}
                  </div>
                )}

                {/* Featured Badge positioned over image */}
                {episode.isFeaturedEpisode && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent">
                    <div className="p-1">
                      <div className="bg-primary/90 text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-sm font-medium flex items-center justify-center gap-1">
                        <Sparkles className="h-2.5 w-2.5" />
                        <span>{t('home.free')}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quiz completion badge - Top right corner */}
                {isCompleted && (
                  <div className="absolute top-1 right-1">
                    <div className="bg-green-500 text-white rounded-md p-1">
                      <CheckCircle2 className="h-3 w-3" />
                    </div>
                  </div>
                )}
              </div>

              {/* 右侧内容 */}
              <div className="flex-1 min-w-0 flex flex-col justify-between h-[60px]">
                {/* 顶部标题 */}
                <h3 className="text-[14px] leading-tight mb-1 line-clamp-2 font-medium group-hover:text-foreground transition-colors duration-200">
                  {displayTitle}
                </h3>

                {/* 底部频道信息 */}
                <div className="flex items-center gap-2 mt-auto">
                  {/* 作者头像 */}
                  <div className="flex-shrink-0 w-5 h-5 rounded-full overflow-hidden bg-primary/15">
                    <Image
                      src={
                        episode.ownerAvatarUrl ||
                        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=20&h=20&fit=crop&crop=face"
                      }
                      alt={episode.author}
                      width={20}
                      height={20}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  {/* 作者名称 */}
                  <span className="text-muted-foreground text-[12px] group-hover:text-muted-foreground/80 transition-colors duration-200 truncate">
                    {episode.author}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </TooltipTrigger>
      {tooltipText && (
        <TooltipContent side="top" className="max-w-xs text-sm">
          <p className="text-sm font-normal">{tooltipText}</p>
        </TooltipContent>
      )}
    </Tooltip>
  );
}

export default function EpisodesSection({
  episodes,
  isLoading,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
}: EpisodesSectionProps) {
  const { t } = useI18n();
  const { user } = useUser();
  const scrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const [isNearEnd, setIsNearEnd] = useState(false);

  // 获取所有episodes的进度数据（一次性调用）
  const { isEpisodeCompleted } = useEpisodeProgress(user?.id);

  // 监听水平滚动（同时支持桌面与移动端容器）
  useEffect(() => {
    const handleScroll = (container: HTMLDivElement) => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const scrollPercentage = (scrollLeft + clientWidth) / Math.max(scrollWidth, 1);

      // 到达 95% 触发加载更多
      if (scrollPercentage > 0.95 && !isNearEnd) {
        setIsNearEnd(true);
        if (hasMore && !isLoadingMore && onLoadMore) {
          onLoadMore();
        }
      } else if (scrollPercentage <= 0.95) {
        setIsNearEnd(false);
      }
    };

    const onDesktopScroll = () => scrollRef.current && handleScroll(scrollRef.current);
    const onMobileScroll = () => mobileScrollRef.current && handleScroll(mobileScrollRef.current);

    const desktop = scrollRef.current;
    const mobile = mobileScrollRef.current;

    if (desktop) desktop.addEventListener('scroll', onDesktopScroll);
    if (mobile) mobile.addEventListener('scroll', onMobileScroll);

    return () => {
      if (desktop) desktop.removeEventListener('scroll', onDesktopScroll);
      if (mobile) mobile.removeEventListener('scroll', onMobileScroll);
    };
  }, [hasMore, isLoadingMore, onLoadMore, isNearEnd]);

  // 添加防护措施，确保 episodes 是数组
  if (isLoading || !episodes || !Array.isArray(episodes)) {
    return (
      <section className="">
        <header className="pl-2 pb-1 sm:pb-4 flex items-center justify-between">
          <h2 className="font-sans text-xl sm:text-2xl font-bold">{t('home.episodes.title')}</h2>
          <Button className="h-auto py-1 text-sm sm:text-normal font-normal" variant="link" asChild>
            <Link href="/episodes">
              <span className="text-xs sm:text-normal font-normal">{t('home.episodes.viewAll')}</span> <ArrowRightIcon className="size-3" />
            </Link>
          </Button>
        </header>

        {/* Loading skeleton: 4 rows for all breakpoints */}
        <div className="w-full overflow-x-auto scrollbar-hide pb-4 max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)]">
          <div style={{ width: "fit-content" }}>
            {Array.from({ length: 4 }).map((_, rowIndex) => (
              <div key={rowIndex} className="flex gap-1.5">
                {Array.from({ length: 4 }).map((_, itemIndex) => (
                  <div key={itemIndex} className="flex-shrink-0 w-[350px] sm:w-[400px] md:w-[450px] lg:w-[500px] p-2">
                    <div className="flex gap-2">
                      <div className="flex-shrink-0 w-[105px] h-[60px] rounded-md bg-muted animate-pulse"></div>
                      <div className="flex-1 min-w-0 py-1 space-y-2">
                        <div className="bg-muted h-4 rounded animate-pulse"></div>
                        <div className="bg-muted h-4 rounded w-3/4 animate-pulse"></div>
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 bg-muted rounded-full animate-pulse"></div>
                          <div className="bg-muted h-3 rounded w-20 animate-pulse"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // 保持原始顺序的 episodes
  const orderedEpisodes =
    episodes && Array.isArray(episodes) ? [...episodes] : [];

  // 如果没有 episodes 数据，显示空状态
  if (orderedEpisodes.length === 0) {
    return (
      <section className="">
        <header className="pl-2 pb-1 sm:pb-4 flex items-center justify-between">
          <h2 className="font-sans text-xl sm:text-2xl font-bold">{t('home.episodes.title')}</h2>
          <Button className="h-auto py-1 text-sm sm:text-normal font-normal" variant="link" asChild>
            <Link href="/episodes">
              <span className="text-xs sm:text-normal font-normal">{t('home.episodes.viewAll')}</span> <ArrowRightIcon className="size-3" />
            </Link>
          </Button>
        </header>

        <div className="text-center py-8 text-muted-foreground">
          <p>{t('home.episodes.empty')}</p>
        </div>
      </section>
    );
  }

  // 将 episodes 按列分配到四行，保持位置稳定
  const firstRow: Episode[] = [];
  const secondRow: Episode[] = [];
  const thirdRow: Episode[] = [];
  const fourthRow: Episode[] = [];

  // 按索引模4的方式分配到不同行，这样新加载的episodes不会影响已有的位置
  orderedEpisodes.forEach((episode, index) => {
    const rowIndex = index % 4;
    if (rowIndex === 0) firstRow.push(episode);
    else if (rowIndex === 1) secondRow.push(episode);
    else if (rowIndex === 2) thirdRow.push(episode);
    else fourthRow.push(episode);
  });

  // 为移动端生成“列”结构：每列包含最多4个（四行各一个）
  const maxColumns = Math.max(
    firstRow.length,
    secondRow.length,
    thirdRow.length,
    fourthRow.length
  );
  const mobileColumns: Episode[][] = Array.from({ length: maxColumns }).map((_, i) => {
    const col: Episode[] = [];
    if (firstRow[i]) col.push(firstRow[i]);
    if (secondRow[i]) col.push(secondRow[i]);
    if (thirdRow[i]) col.push(thirdRow[i]);
    if (fourthRow[i]) col.push(fourthRow[i]);
    return col;
  });

  return (
    <TooltipProvider>
      <section className="">
        <header className="pl-2 pb-1 sm:pb-4 flex items-center justify-between">
          <h2 className="font-sans text-xl sm:text-2xl font-bold">{t('home.episodes.title')}</h2>
          <Button className="h-auto py-1 text-sm sm:text-normal font-normal" variant="link" asChild>
            <Link href="/episodes">
              <span className="text-xs sm:text-normal font-normal">{t('home.episodes.viewAll')}</span> <ArrowRightIcon className="size-3" />
            </Link>
          </Button>
        </header>

        {/* Mobile: 单列吸附，每列4张（纵向堆叠） */}
        <div ref={mobileScrollRef} className="md:hidden w-full overflow-x-auto scrollbar-hide pb-4 max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)] snap-x snap-mandatory">
          <div className="flex gap-3" style={{ width: 'fit-content' }}>
            {mobileColumns.map((col, idx) => (
              <div key={`col-${idx}`} className="flex-shrink-0 w-[calc(100vw-2rem)] snap-start snap-always">
                <div className="flex flex-col gap-0 sm:gap-1.5">
                  {col.map((ep, j) => (
                    <EpisodeItem
                      key={`${ep.shareId}-m-${idx}-${j}`}
                      episode={ep}
                      mobileFull
                      isCompleted={isEpisodeCompleted(ep.shareId)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop/Tablet: 4 rows horizontal */}
        <div ref={scrollRef} className="hidden md:block w-full overflow-x-auto scrollbar-hide pb-4 max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)]">
          <div style={{ width: "fit-content" }}>
            {/* 第一行 */}
            {firstRow.length > 0 && (
              <div className="flex gap-1.5">
                {firstRow.map((episode: Episode, index: number) => (
                  <EpisodeItem
                    key={`${episode.shareId}-${index}`}
                    episode={episode}
                    isCompleted={isEpisodeCompleted(episode.shareId)}
                  />
                ))}
              </div>
            )}

            {/* 第二行 */}
            {secondRow.length > 0 && (
              <div className="flex gap-1.5">
                {secondRow.map((episode: Episode, index: number) => (
                  <EpisodeItem
                    key={`${episode.shareId}-${index}`}
                    episode={episode}
                    isCompleted={isEpisodeCompleted(episode.shareId)}
                  />
                ))}
              </div>
            )}

            {/* 第三行 */}
            {thirdRow.length > 0 && (
              <div className="flex gap-1.5">
                {thirdRow.map((episode: Episode, index: number) => (
                  <EpisodeItem
                    key={`${episode.shareId}-${index}`}
                    episode={episode}
                    isCompleted={isEpisodeCompleted(episode.shareId)}
                  />
                ))}
              </div>
            )}

            {/* 第四行 */}
            {fourthRow.length > 0 && (
              <div className="flex gap-1.5">
                {fourthRow.map((episode: Episode, index: number) => (
                  <EpisodeItem
                    key={`${episode.shareId}-${index}`}
                    episode={episode}
                    isCompleted={isEpisodeCompleted(episode.shareId)}
                  />
                ))}
              </div>
            )}

            {/* 加载更多指示器 */}
            {/* {(hasMore || isLoadingMore) && (
            <div className="flex-shrink-0 w-24 flex items-center justify-center py-8">
              {isLoadingMore ? (
                <SpinnerIcon size="sm" />
              ) : (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onLoadMore}
                  disabled={!hasMore}
                >
                  More
                </Button>
              )}
            </div>
          )} */}
          </div>
        </div>
      </section>
    </TooltipProvider>
  );
}
